import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import axios from 'axios';
import { Store, Rocket, Palette, Package, GraduationCap, CheckSquare, RefreshCw, Search, Plus, X, Clock, CheckCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const ZOBOX_PRIMARY = '#2b3380';

function StoreSetup({ user, onLogout }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');
  const [setups, setSetups] = useState([]);
  const [selectedSetup, setSelectedSetup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [checklist, setChecklist] = useState([]);

  const tabs = [
    { id: 'overview', name: 'Overview', icon: Store },
    { id: 'design', name: 'Design Approval', icon: Palette },
    { id: 'stock', name: 'Stock Allocation', icon: Package },
    { id: 'training', name: 'Training', icon: GraduationCap },
    { id: 'checklist', name: 'Launch Checklist', icon: CheckSquare }
  ];

  useEffect(() => {
    fetchSetups();
  }, []);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) setActiveTab(tab);
  }, [searchParams]);

  useEffect(() => {
    if (selectedSetup && activeTab === 'checklist') {
      fetchChecklist();
    }
  }, [selectedSetup, activeTab]);

  const fetchSetups = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/store-setup`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSetups(response.data.setups || []);
      if (response.data.setups?.length > 0) {
        setSelectedSetup(response.data.setups[0]);
      }
    } catch (error) {
      console.error('Error fetching setups:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchChecklist = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/launch-checklist/${selectedSetup.setup_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChecklist(response.data.checklist_items || []);
    } catch (error) {
      console.error('Error fetching checklist:', error);
    }
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };

  const getStatusColor = (status) => {
    const colors = {
      'In Progress': 'border-l-yellow-500 bg-yellow-50',
      'Pending': 'border-l-orange-500 bg-orange-50',
      'Completed': 'border-l-green-500 bg-green-50',
      'Not Started': 'border-l-gray-400 bg-gray-50'
    };
    return colors[status] || 'border-l-gray-400 bg-gray-50';
  };

  const getStatusBadge = (status) => {
    const colors = {
      'In Progress': 'bg-yellow-500 text-white',
      'Pending': 'bg-orange-500 text-white',
      'Completed': 'bg-green-500 text-white',
      'Not Started': 'bg-gray-400 text-white'
    };
    return colors[status] || 'bg-gray-400 text-white';
  };

  // Stats calculation
  const stats = {
    total: setups.length,
    inProgress: setups.filter(s => s.setup_status === 'In Progress').length,
    pending: setups.filter(s => s.setup_status === 'Pending' || s.setup_status === 'Not Started').length,
    completed: setups.filter(s => s.setup_status === 'Completed').length
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="p-6">
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" />
            <p className="mt-3 text-gray-500">Loading store setups...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold" style={{color: ZOBOX_PRIMARY}}>🚀 Store Setup & Launch</h1>
            <p className="text-gray-600 mt-2">Manage store setup from design to launch</p>
          </div>
          <div className="flex gap-3 items-center">
            {setups.length > 0 && (
              <select
                value={selectedSetup?.setup_id || ''}
                onChange={(e) => {
                  const setup = setups.find(s => s.setup_id === e.target.value);
                  setSelectedSetup(setup);
                }}
                className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 min-w-[200px]"
              >
                {setups.map(setup => (
                  <option key={setup.setup_id} value={setup.setup_id}>
                    {setup.store_name} ({setup.store_code})
                  </option>
                ))}
              </select>
            )}
            <button
              onClick={fetchSetups}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4 flex items-center gap-4">
            <div className="p-3 rounded-full" style={{backgroundColor: '#e8eaf6'}}>
              <Rocket className="w-6 h-6" style={{color: ZOBOX_PRIMARY}} />
            </div>
            <div>
              <p className="text-2xl font-bold" style={{color: ZOBOX_PRIMARY}}>{stats.total}</p>
              <p className="text-sm text-gray-500">Total Setups</p>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-yellow-100">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-yellow-600">{stats.inProgress}</p>
              <p className="text-sm text-gray-500">In Progress</p>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-orange-100">
              <AlertTriangle className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-orange-600">{stats.pending}</p>
              <p className="text-sm text-gray-500">Pending</p>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-green-100">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
              <p className="text-sm text-gray-500">Completed</p>
            </div>
          </div>
        </div>

        {setups.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <Rocket className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <p className="text-xl font-medium text-gray-500 mb-2">No Store Setups Found</p>
            <p className="text-gray-400">Store setups will appear here when franchisees reach the setup phase</p>
          </div>
        ) : (
          <>
            {/* Selected Setup Card */}
            {selectedSetup && (
              <div className="bg-white rounded-lg shadow-md p-4 mb-6 border-l-4" style={{borderLeftColor: ZOBOX_PRIMARY}}>
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-full" style={{backgroundColor: '#e8eaf6'}}>
                      <Store className="w-6 h-6" style={{color: ZOBOX_PRIMARY}} />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold" style={{color: ZOBOX_PRIMARY}}>{selectedSetup.store_name}</h2>
                      <p className="text-sm text-gray-500">{selectedSetup.store_code} • Manager: {selectedSetup.setup_manager}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-sm text-gray-500">Expected Launch</p>
                      <p className="font-medium" style={{color: ZOBOX_PRIMARY}}>
                        {selectedSetup.expected_launch_date ? new Date(selectedSetup.expected_launch_date).toLocaleDateString('en-IN') : 'TBD'}
                      </p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(selectedSetup.setup_status)}`}>
                      {selectedSetup.setup_status}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Tabs */}
            <div className="bg-white rounded-lg shadow-md mb-6">
              <div className="flex border-b overflow-x-auto">
                {tabs.map(tab => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => handleTabChange(tab.id)}
                      className={`px-6 py-3 font-medium flex items-center gap-2 whitespace-nowrap ${activeTab === tab.id ? 'border-b-2' : 'text-gray-500'}`}
                      style={activeTab === tab.id ? {borderColor: ZOBOX_PRIMARY, color: ZOBOX_PRIMARY} : {}}
                    >
                      <Icon className="w-4 h-4" /> {tab.name}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Tab Content */}
            {activeTab === 'overview' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Progress Overview */}
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h3 className="text-lg font-bold mb-4" style={{color: ZOBOX_PRIMARY}}>Setup Progress</h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Palette className="w-5 h-5" style={{color: ZOBOX_PRIMARY}} />
                        <span>Design Approval</span>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs ${selectedSetup?.design_approved ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {selectedSetup?.design_approved ? 'Completed' : 'Pending'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Store className="w-5 h-5" style={{color: ZOBOX_PRIMARY}} />
                        <span>Signage Ready</span>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs ${selectedSetup?.signage_ready ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {selectedSetup?.signage_ready ? 'Completed' : 'Pending'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Package className="w-5 h-5" style={{color: ZOBOX_PRIMARY}} />
                        <span>Inventory Allocated</span>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs ${selectedSetup?.inventory_allocated ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {selectedSetup?.inventory_allocated ? 'Completed' : 'Pending'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <GraduationCap className="w-5 h-5" style={{color: ZOBOX_PRIMARY}} />
                        <span>Staff Training</span>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs ${selectedSetup?.training_completed ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {selectedSetup?.training_completed ? 'Completed' : 'Pending'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <CheckSquare className="w-5 h-5" style={{color: ZOBOX_PRIMARY}} />
                        <span>Compliance Cleared</span>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs ${selectedSetup?.compliance_cleared ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {selectedSetup?.compliance_cleared ? 'Completed' : 'Pending'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Timeline */}
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h3 className="text-lg font-bold mb-4" style={{color: ZOBOX_PRIMARY}}>Setup Timeline</h3>
                  <div className="space-y-4">
                    <div className="flex gap-4">
                      <div className="flex flex-col items-center">
                        <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
                          <CheckCircle className="w-4 h-4 text-white" />
                        </div>
                        <div className="w-0.5 h-full bg-gray-200 my-2"></div>
                      </div>
                      <div>
                        <p className="font-medium">Setup Created</p>
                        <p className="text-sm text-gray-500">{selectedSetup?.created_at ? new Date(selectedSetup.created_at).toLocaleDateString('en-IN') : 'N/A'}</p>
                      </div>
                    </div>
                    <div className="flex gap-4">
                      <div className="flex flex-col items-center">
                        <div className={`w-8 h-8 rounded-full ${selectedSetup?.design_approved ? 'bg-green-500' : 'bg-yellow-500'} flex items-center justify-center`}>
                          <Palette className="w-4 h-4 text-white" />
                        </div>
                        <div className="w-0.5 h-full bg-gray-200 my-2"></div>
                      </div>
                      <div>
                        <p className="font-medium">Design Phase</p>
                        <p className="text-sm text-gray-500">{selectedSetup?.design_approved ? 'Approved' : 'In Progress'}</p>
                      </div>
                    </div>
                    <div className="flex gap-4">
                      <div className="flex flex-col items-center">
                        <div className={`w-8 h-8 rounded-full ${selectedSetup?.inventory_allocated ? 'bg-green-500' : 'bg-gray-300'} flex items-center justify-center`}>
                          <Package className="w-4 h-4 text-white" />
                        </div>
                        <div className="w-0.5 h-full bg-gray-200 my-2"></div>
                      </div>
                      <div>
                        <p className="font-medium">Stock Allocation</p>
                        <p className="text-sm text-gray-500">{selectedSetup?.inventory_allocated ? 'Done' : 'Pending'}</p>
                      </div>
                    </div>
                    <div className="flex gap-4">
                      <div className="flex flex-col items-center">
                        <div className={`w-8 h-8 rounded-full ${selectedSetup?.training_completed ? 'bg-green-500' : 'bg-gray-300'} flex items-center justify-center`}>
                          <GraduationCap className="w-4 h-4 text-white" />
                        </div>
                        <div className="w-0.5 h-full bg-gray-200 my-2"></div>
                      </div>
                      <div>
                        <p className="font-medium">Staff Training</p>
                        <p className="text-sm text-gray-500">{selectedSetup?.training_completed ? 'Completed' : 'Pending'}</p>
                      </div>
                    </div>
                    <div className="flex gap-4">
                      <div className="flex flex-col items-center">
                        <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center">
                          <Rocket className="w-4 h-4 text-white" />
                        </div>
                      </div>
                      <div>
                        <p className="font-medium">Store Launch</p>
                        <p className="text-sm text-gray-500">Expected: {selectedSetup?.expected_launch_date ? new Date(selectedSetup.expected_launch_date).toLocaleDateString('en-IN') : 'TBD'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'design' && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-bold mb-4" style={{color: ZOBOX_PRIMARY}}>Design Approval Status</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className={`p-4 rounded-lg border-l-4 ${selectedSetup?.design_approved ? 'border-l-green-500 bg-green-50' : 'border-l-yellow-500 bg-yellow-50'}`}>
                    <p className="font-medium">Interior Design</p>
                    <p className="text-sm text-gray-500">{selectedSetup?.design_approved ? 'Approved' : 'Pending Approval'}</p>
                  </div>
                  <div className={`p-4 rounded-lg border-l-4 ${selectedSetup?.signage_ready ? 'border-l-green-500 bg-green-50' : 'border-l-yellow-500 bg-yellow-50'}`}>
                    <p className="font-medium">Signage Design</p>
                    <p className="text-sm text-gray-500">{selectedSetup?.signage_ready ? 'Ready' : 'In Progress'}</p>
                  </div>
                  <div className="p-4 rounded-lg border-l-4 border-l-gray-400 bg-gray-50">
                    <p className="font-medium">Branding Elements</p>
                    <p className="text-sm text-gray-500">Pending</p>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'stock' && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-bold mb-4" style={{color: ZOBOX_PRIMARY}}>Stock Allocation</h3>
                {selectedSetup?.inventory_allocated ? (
                  <div className="p-4 bg-green-50 rounded-lg border-l-4 border-l-green-500">
                    <p className="font-medium text-green-700">Initial stock has been allocated</p>
                    <p className="text-sm text-gray-600">The store has received initial inventory allocation</p>
                  </div>
                ) : (
                  <div className="p-4 bg-yellow-50 rounded-lg border-l-4 border-l-yellow-500">
                    <p className="font-medium text-yellow-700">Stock allocation pending</p>
                    <p className="text-sm text-gray-600">Initial inventory will be allocated once design phase is complete</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'training' && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-bold mb-4" style={{color: ZOBOX_PRIMARY}}>Staff Training</h3>
                {selectedSetup?.staff_hired ? (
                  <div className="space-y-4">
                    <div className={`p-4 rounded-lg border-l-4 ${selectedSetup?.training_completed ? 'border-l-green-500 bg-green-50' : 'border-l-yellow-500 bg-yellow-50'}`}>
                      <p className="font-medium">{selectedSetup?.training_completed ? 'Training Completed' : 'Training In Progress'}</p>
                      <p className="text-sm text-gray-600">Staff members: {selectedSetup?.staff_count || 'TBD'}</p>
                    </div>
                  </div>
                ) : (
                  <div className="p-4 bg-orange-50 rounded-lg border-l-4 border-l-orange-500">
                    <p className="font-medium text-orange-700">Staff hiring pending</p>
                    <p className="text-sm text-gray-600">Training will begin once staff is hired</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'checklist' && (
              <div className="space-y-4">
                {checklist.length === 0 ? (
                  <div className="bg-white rounded-lg shadow-md p-12 text-center">
                    <CheckSquare className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <p className="text-xl font-medium text-gray-500 mb-2">Launch Checklist</p>
                    <p className="text-gray-400">Checklist items will appear here</p>
                  </div>
                ) : (
                  checklist.map((item, idx) => (
                    <div key={idx} className={`bg-white rounded-lg shadow-md p-4 border-l-4 ${item.status === 'Completed' ? 'border-l-green-500' : 'border-l-yellow-500'}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {item.status === 'Completed' ? (
                            <CheckCircle className="w-5 h-5 text-green-500" />
                          ) : (
                            <Clock className="w-5 h-5 text-yellow-500" />
                          )}
                          <div>
                            <p className="font-medium">{item.item_name}</p>
                            <p className="text-sm text-gray-500">{item.category}</p>
                          </div>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs ${item.status === 'Completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                          {item.status}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}

export default StoreSetup;
